import type {
  Category,
  ClaimFiledWithType,
  ClaimStatus,
  ResolutionType,
  ResponsibleParty,
  Stage,
} from '../api/types'

/**
 * Single source of truth for stage/category/resolution labels + variants
 * + allowed transitions. Mirrors `middleware/app/models/material_disposition.py`'s
 * frozensets 1:1 — keep the two in sync by hand (same discipline as
 * returns-rma's stage-meta.ts).
 */

export const ALL_STAGES: Stage[] = [
  'open',
  'investigating',
  'resolution_pending',
  'claim_pending',
  'resolved',
  'cancelled',
]

export const TERMINAL_STAGES: ReadonlySet<Stage> = new Set(['resolved', 'cancelled'])

// Kanban column order — board order rather than enum order. `claim_pending`
// sits between Disposition in Progress and Closed.
export const BOARD_STAGES: Stage[] = [
  'open',
  'investigating',
  'resolution_pending',
  'claim_pending',
  'resolved',
  'cancelled',
]

// Internal keys predate this relabeling (`open`, `investigating`, etc.) —
// only the display labels changed; `claim_pending` is the one genuinely
// new stage. Keep in sync with `_STAGE_LABELS` in
// `app/services/material_disposition.py` by hand.
export const STAGE_LABELS: Record<Stage, string> = {
  open: 'New',
  investigating: 'Evidence Investigation',
  resolution_pending: 'Disposition in Progress',
  claim_pending: 'Claim Pending',
  resolved: 'Closed',
  cancelled: 'Cancelled',
}

export const STAGE_SHORT_LABELS: Record<Stage, string> = {
  open: 'New',
  investigating: 'Evidence Investigation',
  resolution_pending: 'Disposition',
  claim_pending: 'Claim Pending',
  resolved: 'Closed',
  cancelled: 'Cancelled',
}

export const STAGE_VARIANT: Record<
  Stage,
  'neutral' | 'success' | 'warning' | 'danger' | 'info'
> = {
  open: 'neutral',
  investigating: 'info',
  resolution_pending: 'warning',
  claim_pending: 'warning',
  resolved: 'success',
  cancelled: 'danger',
}

/**
 * Permissive transition table — mirrors `ALLOWED_TRANSITIONS` in
 * `app/models/material_disposition.py`: any non-terminal stage can move
 * to any other stage; terminals can only undo back to `open`.
 */
export function transitionAllowed(from: Stage, to: Stage): boolean {
  if (from === to) return false
  if (TERMINAL_STAGES.has(from)) return to === 'open'
  return true
}

export const CATEGORY_LABELS: Record<Category, string> = {
  damaged: 'Damaged',
  lost: 'Lost',
  quarantined: 'Quarantined',
  not_sellable_off_spec: 'Not sellable / off-spec',
  expired_near_expiry: 'Expired / near-expiry',
  recalled: 'Recalled',
}

export const ALL_CATEGORIES: Category[] = [
  'damaged',
  'lost',
  'quarantined',
  'not_sellable_off_spec',
  'expired_near_expiry',
  'recalled',
]

export const RESOLUTION_LABELS: Record<ResolutionType, string> = {
  write_off: 'Write-off',
  vendor_claim: 'Vendor claim filed',
  return_to_vendor: 'Returned to vendor',
  insurance_claim: 'Insurance claim filed',
  internal_use: 'Downgraded to internal use',
  customer_goodwill_credit: 'Customer goodwill credit',
  released: 'Released (back to sellable)',
}

export const ALL_RESOLUTION_TYPES: ResolutionType[] = [
  'write_off',
  'vendor_claim',
  'return_to_vendor',
  'insurance_claim',
  'internal_use',
  'customer_goodwill_credit',
  'released',
]

export const RESPONSIBLE_PARTY_LABELS: Record<ResponsibleParty, string> = {
  vendor: 'Vendor',
  internal: 'Internal',
  customer: 'Customer',
  carrier: 'Carrier',
  unknown: 'Unknown',
}

export const ALL_RESPONSIBLE_PARTIES: ResponsibleParty[] = [
  'vendor',
  'internal',
  'customer',
  'carrier',
  'unknown',
]

export const CLAIM_FILED_WITH_TYPE_LABELS: Record<ClaimFiledWithType, string> = {
  carrier: 'Carrier',
  vendor: 'Vendor',
  insurance: 'Insurance',
  customer: 'Customer',
  other: 'Other',
}

export const ALL_CLAIM_FILED_WITH_TYPES: ClaimFiledWithType[] = [
  'carrier',
  'vendor',
  'insurance',
  'customer',
  'other',
]

export const CLAIM_STATUS_LABELS: Record<ClaimStatus, string> = {
  filed: 'Filed',
  acknowledged: 'Acknowledged',
  approved: 'Approved',
  denied: 'Denied',
  paid: 'Paid',
}

// Lifecycle order, not alphabetical — matches ALL_RESOLUTION_TYPES.
export const ALL_CLAIM_STATUSES: ClaimStatus[] = [
  'filed',
  'acknowledged',
  'approved',
  'denied',
  'paid',
]

export const CLAIM_STATUS_VARIANT: Record<
  ClaimStatus,
  'neutral' | 'success' | 'warning' | 'danger' | 'info'
> = {
  filed: 'neutral',
  acknowledged: 'info',
  approved: 'success',
  denied: 'danger',
  paid: 'success',
}

/** The claim-relevant subset of a case, so callers can pass a CaseSummary
 *  or a CaseDetail without either being imported here. */
export interface ClaimContextFields {
  stage: Stage
  resolution_type: ResolutionType | null
  responsible_party: ResponsibleParty | null
  claim_filed_with_type: ClaimFiledWithType | null
  claim_filed_with_name: string | null
  claim_number: string | null
  claim_filed_date: string | null
  claim_status: ClaimStatus | null
  claim_amount_filed: string | null
  claim_amount_recovered: string | null
  bol_number: string | null
  pro_number: string | null
}

/**
 * Whether a case looks claim-related, i.e. whether the drawer expands the
 * Claim section by default rather than collapsing it behind "Record claim".
 *
 * Clause 4 is the important one: `responsible_party === 'carrier'` has no
 * matching `resolution_type` (there is no `carrier_claim` value), so the
 * old `resolution_type in (vendor_claim, insurance_claim)` gate made
 * carrier claims — the exact thing the generated PDF is named after —
 * impossible to record.
 */
export function hasClaimContext(c: ClaimContextFields): boolean {
  // 1. Any claim data already recorded. Populated data is never hidden,
  //    whoever wrote it (UI, API, a future CARLA tool).
  if (
    c.claim_filed_with_type ||
    c.claim_filed_with_name ||
    c.claim_number ||
    c.claim_filed_date ||
    c.claim_status ||
    c.claim_amount_filed ||
    c.claim_amount_recovered ||
    c.bol_number ||
    c.pro_number
  ) {
    return true
  }
  // 2. The board says a claim is in flight.
  if (c.stage === 'claim_pending') return true
  // 3. The chosen disposition IS a claim.
  if (c.resolution_type === 'vendor_claim' || c.resolution_type === 'insurance_claim') return true
  // 4. Fault is on the carrier.
  if (c.responsible_party === 'carrier') return true
  return false
}

/** Display form of "filed with": name wins, type label is the fallback. */
export function filedWithLabel(c: {
  claim_filed_with_type: ClaimFiledWithType | null
  claim_filed_with_name: string | null
}): string | null {
  if (c.claim_filed_with_name) return c.claim_filed_with_name
  if (c.claim_filed_with_type) return CLAIM_FILED_WITH_TYPE_LABELS[c.claim_filed_with_type]
  return null
}
